import React from 'react';
import LytTooltip from '../tooltip/LytTooltip';
import styled, { useTheme } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import LytMenuItem from './LytMenuItem';
import PropTypes from 'prop-types';
import LytIcon from '../icon/LytIcon.jsx';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../../styled/theme/typography-utils.js';
const StyledLink = styled(Link)`
    > div {
        height: 40px;
        padding: ${(props) => (props.$collapsed ? '8px 0' : '8px 16px')};
        justify-content: ${(props) =>
            props.$collapsed ? 'center' : 'flex-start'};
    }
    p {
        color: inherit;
        margin-left: 8px;
    }
    .lyt-sidebar-icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
const StyledLinkText = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)};
`;
const StyledContainer = styled.div`
    p {
        color: inherit;
        margin-left: 8px;
    }
    .lyt-sidebar-icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .lyt-sidebar-dropdown-icon-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }
`;
const menuItemIsSelected = (path = '', key = '') => {
    const lowerKey = key.toLowerCase();
    return path.includes(`/${lowerKey}/`) || path.endsWith(`/${lowerKey}`);
};

/**
 * @description The sidebar menu item component, either used as a link or a side dropdown target
 * @param {object} props.children - The children of the menu item
 * @param {string} props.id - The id of the menu item
 * @param {boolean} props.selected - Whether the menu item is selected
 * @param {string} props.iconName - The name of the icon to display
 * @param {boolean} props.collapsed - Whether the menu item is collapsed
 * @param {string} props.title - The title of the menu item
 * @param {string} props.href - The href of the menu item
 * @param {function} props.onClick - The onClick function of the menu item
 */
const LytSidebarMenuItem = ({
    children = null,
    id = '',
    iconName = null,
    noIconColor = false,
    collapsed = false,
    title = '',
    href = '/',
    isDropdown = false,
    selected = false,
    onClick = () => {},
}) => {
    const theme = useTheme();
    const { pathname } = useLocation();
    const textClassName = collapsed ? 'lyt-sidebar-menu-item-collapsed' : '';

    // Use sidebar-specific icon colors from theme
    const sidebarIconColor = theme?.components?.lyt_menu_item?.icon?.default;
    const sidebarIconSelectedColor =
        theme?.components?.lyt_menu_item?.icon?.selected;
    const dropdownTargetIconColor = selected
        ? sidebarIconSelectedColor
        : sidebarIconColor;
    const isLinkSelected = menuItemIsSelected(pathname, id);
    const linkIconColor = isLinkSelected
        ? sidebarIconSelectedColor
        : sidebarIconColor;
    return !isDropdown ? (
        <LytTooltip content={title} position="right">
            <StyledLink key={href + title} to={href} $collapsed={collapsed}>
                <LytMenuItem
                    id={`${id}-${pathname}`}
                    onClick={onClick}
                    selected={isLinkSelected}
                    backgroundColor="transparent">
                    <>
                        {iconName ? (
                            <div className="lyt-sidebar-icon-container">
                                <LytIcon
                                    name={iconName}
                                    {...(!noIconColor && {
                                        color: linkIconColor,
                                    })}
                                />
                            </div>
                        ) : null}
                        {!collapsed && (
                            <StyledLinkText className={textClassName}>
                                {children}
                            </StyledLinkText>
                        )}
                    </>
                </LytMenuItem>
            </StyledLink>
        </LytTooltip>
    ) : (
        <StyledContainer $collapsed={collapsed}>
            <LytTooltip content={title} position="right">
                <LytMenuItem
                    id={id}
                    selected={selected}
                    backgroundColor="transparent"
                    onClick={onClick}>
                    <>
                        {iconName ? (
                            <div className="lyt-sidebar-icon-container">
                                <LytIcon
                                    name={iconName}
                                    color={dropdownTargetIconColor}
                                />
                            </div>
                        ) : null}
                        {!collapsed && (
                            <p className={textClassName}>{children}</p>
                        )}
                        {!collapsed ? (
                            <div className="lyt-sidebar-dropdown-icon-container">
                                <LytIcon
                                    name="chevron-right"
                                    color={dropdownTargetIconColor}
                                />
                            </div>
                        ) : null}
                    </>
                </LytMenuItem>
            </LytTooltip>
        </StyledContainer>
    );
};
LytSidebarMenuItem.propTypes = {
    children: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
    selected: PropTypes.bool,
    iconName: PropTypes.string,
    noIconColor: PropTypes.bool,
    collapsed: PropTypes.bool,
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    isDropdown: PropTypes.bool,
    onClick: PropTypes.func,
};
export default LytSidebarMenuItem;
